import { authSplitApi } from '../helpers/slice.helpers';
import {
  TCreatedPartnerApiIntegrationAppDB,
  TCreatedPartnerApiIntegrationAppUI,
  TCreatePartnerApiIntegrationAppUI,
  TCreatePartnerApiAppWebhookSubscriptionUI,
  TGetPartnerApiAppLandlordsParamsUI,
  TPartnerApiAppLandlordDB,
  TPartnerApiAppLandlordUI,
  TPartnerApiAppWebhookSubscriptionDB,
  TPartnerApiAppWebhookSubscriptionUI,
  TPartnerApiIntegrationAppDB,
  TPartnerApiIntegrationAppUI,
  TRemovePartnerApiAppWebhookSubscriptionUI,
  TSetPartnerApiAppFutureAccessUI,
  TSetPartnerApiAppVisibilityUI,
  TUpdatePartnerApiAppDescriptionUI,
  TUpdatePartnerApiAppLogoUI,
  TUpdatePartnerApiAppNameUI,
} from 'data/types/partnerApiIntegrationApp.types';
import { TPageableDataWithContentUi } from 'data/types/Table.types';
import {
  createdPartnerApiIntegrationAppConverter,
  createPartnerApiIntegrationAppConverter,
  createPartnerApiAppWebhookSubscriptionConverter,
  partnerApiAppLandlordsConverter,
  partnerApiAppWebhookSubscriptionConverter,
  partnerApiIntegrationAppsConverter,
} from 'data/convertors/partnerApiIntegrationApp.converters';

const BASE_URL = 'partner-api-integrations';

export const partnerApiIntegrationAppApi = authSplitApi('partnerApiIntegrationApp', [
  'partnerApiIntegrationApps',
  'partnerApiAppLandlords',
  'partnerApiAppWebhooks',
]).injectEndpoints({
  endpoints: build => ({
    getPartnerApiIntegrationApps: build.query<TPartnerApiIntegrationAppUI[], void>({
      query() {
        return {
          url: `${BASE_URL}/partners`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPartnerApiIntegrationAppDB[]) => partnerApiIntegrationAppsConverter.fromDb(data),
      providesTags: ['partnerApiIntegrationApps'],
    }),

    createPartnerApiIntegrationApp: build.mutation<
      TCreatedPartnerApiIntegrationAppUI,
      TCreatePartnerApiIntegrationAppUI
    >({
      query(body) {
        return {
          url: `${BASE_URL}/partners`,
          method: 'POST',
          body: createPartnerApiIntegrationAppConverter.toDb(body),
        };
      },
      transformResponse: (data: TCreatedPartnerApiIntegrationAppDB) =>
        createdPartnerApiIntegrationAppConverter.fromDb(data),
      invalidatesTags: ['partnerApiIntegrationApps'],
    }),

    getPartnerApiAppPropertyManagements: build.query<
      TPageableDataWithContentUi<TPartnerApiAppLandlordUI[]>,
      TGetPartnerApiAppLandlordsParamsUI
    >({
      query({ appId, page, size, search, statuses, connected, visible }) {
        return {
          url: `${BASE_URL}/${appId}/property-managements`,
          method: 'GET',
          params: {
            page,
            size,
            ...(search ? { search } : {}),
            ...(statuses && statuses.length ? { statuses } : {}),
            ...(connected !== undefined ? { connected } : {}),
            ...(visible !== undefined ? { visible } : {}),
          },
        };
      },
      transformResponse: (data: TPageableDataWithContentUi<TPartnerApiAppLandlordDB[]>) => ({
        ...data,
        content: partnerApiAppLandlordsConverter.fromDb(data.content),
      }),
      providesTags: ['partnerApiAppLandlords'],
    }),

    setPartnerApiAppVisibility: build.mutation<void, TSetPartnerApiAppVisibilityUI>({
      query({ appId, propertyManagementId, visible }) {
        return {
          url: `${BASE_URL}/${appId}/property-managements/${propertyManagementId}`,
          method: 'PATCH',
          body: { visible },
        };
      },
      invalidatesTags: ['partnerApiAppLandlords'],
    }),

    setPartnerApiAppFutureAccess: build.mutation<void, TSetPartnerApiAppFutureAccessUI>({
      query({ appId, includeFuture }) {
        return {
          url: `${BASE_URL}/${appId}/visibility`,
          method: 'PATCH',
          body: { visibleToFuturePropertyManagements: includeFuture },
        };
      },
      invalidatesTags: ['partnerApiIntegrationApps'],
    }),

    bulkSetPartnerApiAppVisibility: build.mutation<
      void,
      {
        appId: string;
        propertyManagementIds: string[];
        allSelected: boolean;
        visible: boolean;
        filters: { search?: string; statuses?: string[]; connected?: boolean; visible?: boolean };
      }
    >({
      query({ appId, propertyManagementIds, allSelected, visible, filters }) {
        return {
          url: `${BASE_URL}/${appId}/property-managements/bulk`,
          method: 'PATCH',
          body: {
            visible,
            allSelected,
            propertyManagementIds,
            search: {
              nameContains: filters.search,
              statusIn: filters.statuses,
              connected: filters.connected,
              visible: filters.visible,
            },
          },
        };
      },
      invalidatesTags: ['partnerApiAppLandlords'],
    }),

    updatePartnerApiAppName: build.mutation<void, TUpdatePartnerApiAppNameUI>({
      query({ appId, name }) {
        return {
          url: `${BASE_URL}/${appId}/name`,
          method: 'PATCH',
          body: { name },
        };
      },
      invalidatesTags: ['partnerApiIntegrationApps'],
    }),

    updatePartnerApiAppDescription: build.mutation<void, TUpdatePartnerApiAppDescriptionUI>({
      query({ appId, description }) {
        return {
          url: `${BASE_URL}/${appId}/description`,
          method: 'PATCH',
          body: { description },
        };
      },
      invalidatesTags: ['partnerApiIntegrationApps'],
    }),

    updatePartnerApiAppLogo: build.mutation<void, TUpdatePartnerApiAppLogoUI>({
      query({ appId, logoUrl }) {
        return {
          url: `${BASE_URL}/${appId}/logo`,
          method: 'PATCH',
          body: { logoUrl },
        };
      },
      invalidatesTags: ['partnerApiIntegrationApps'],
    }),

    getPartnerApiAppWebhookEventTypes: build.query<string[], void>({
      query() {
        return {
          url: `${BASE_URL}/webhook/event-types`,
          method: 'GET',
        };
      },
    }),

    getPartnerApiAppWebhookSubscriptions: build.query<TPartnerApiAppWebhookSubscriptionUI[], string>({
      query(appId) {
        return {
          url: `${BASE_URL}/${appId}/webhook/subscriptions`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPartnerApiAppWebhookSubscriptionDB[]) =>
        (data || []).map(partnerApiAppWebhookSubscriptionConverter.fromDb),
      providesTags: ['partnerApiAppWebhooks'],
    }),

    createPartnerApiAppWebhookSubscription: build.mutation<void, TCreatePartnerApiAppWebhookSubscriptionUI>({
      query(payload) {
        return {
          url: `${BASE_URL}/${payload.appId}/webhook/subscriptions`,
          method: 'POST',
          body: createPartnerApiAppWebhookSubscriptionConverter.toDb(payload),
        };
      },
      invalidatesTags: ['partnerApiAppWebhooks'],
    }),

    removePartnerApiAppWebhookSubscription: build.mutation<void, TRemovePartnerApiAppWebhookSubscriptionUI>({
      query({ appId, subscriptionId }) {
        return {
          url: `${BASE_URL}/${appId}/webhook/subscriptions/${subscriptionId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['partnerApiAppWebhooks'],
    }),
  }),
});

export const {
  useGetPartnerApiIntegrationAppsQuery,
  useCreatePartnerApiIntegrationAppMutation,
  useGetPartnerApiAppPropertyManagementsQuery,
  useSetPartnerApiAppVisibilityMutation,
  useSetPartnerApiAppFutureAccessMutation,
  useBulkSetPartnerApiAppVisibilityMutation,
  useUpdatePartnerApiAppNameMutation,
  useUpdatePartnerApiAppDescriptionMutation,
  useUpdatePartnerApiAppLogoMutation,
  useGetPartnerApiAppWebhookEventTypesQuery,
  useGetPartnerApiAppWebhookSubscriptionsQuery,
  useCreatePartnerApiAppWebhookSubscriptionMutation,
  useRemovePartnerApiAppWebhookSubscriptionMutation,
} = partnerApiIntegrationAppApi;